import React, { useRef, useState, useEffect } from "react";
import { Paper, Typography, Box, Button, CircularProgress } from "@mui/material";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import DownloadIcon from "@mui/icons-material/Download";
import {format2Date, LOGO_ACCESS_PROD, REACT_APP_LIFERAY_HOST, VENTE_RELATION_ACCESS} from "../services/utils";
import swal from "sweetalert";

// --- Configuration A6 (105mm x 148mm) ---
const BADGE_WIDTH = "105mm";
const BADGE_HEIGHT = "148mm";

// CSS pour forcer le format A6 lors de l'impression native (Ctrl+P)
const printStyleA6 = `
  @media print {
    @page {
      size: 105mm 148mm;
      margin: 0;
    }
    html, body {
      width: 105mm;
      height: 148mm;
    }
    body {
      -webkit-print-color-adjust: exact;
    }
  }
`;

const BadgeA6 = ({ infosParticipant }) => {
    // Fallback sécurisé si infosParticipant est null
    const participant = infosParticipant || {

        nom: "NOM",
        prenoms: "Prénom",
        reference: "REF12345",
        profession: { name: "Fonction" }
    };

    const badgeRef = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        const styleEl = document.createElement("style");
        styleEl.innerHTML = printStyleA6;
        document.head.appendChild(styleEl);
        return () => document.head.removeChild(styleEl);
    }, [infosParticipant]);
    console.log("🚀 ~ BadgeA6 ~ infosParticipant:", infosParticipant)

    const handleDownloadPDF = async () => {
        setIsDownloading(true);
        try {
            const input = badgeRef.current;
            if (!input) throw new Error("Référence du badge introuvable");

            // 1. Capture Haute Qualité (scale 3 pour netteté du texte)
            const canvas = await html2canvas(input, {
                scale: 4, // Augmenté pour éviter le flou à l'impression
                useCORS: true,
                allowTaint: true,
                backgroundColor: "#ffffff",
            });

            const imgData = canvas.toDataURL("image/png");

            // 2. Création du PDF au format A6
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a6" // Format natif A6
            });

            // Dimensions exactes A6
            const pdfWidth = 105;
            const pdfHeight = 148;

            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

            const fileName = `Badge_${participant.reference || "invite"}.pdf`;
            pdf.save(fileName);
            swal("Succès", "Badge généré avec succès !", "success");

        } catch (error) {
            console.error(error);
            swal("Erreur", "Erreur lors de la génération du badge", "error");
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, py: 4 }}>

            {/* Bouton d'action */}
            <Button
                variant="contained"
                onClick={handleDownloadPDF}
                disabled={isDownloading}
                startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                sx={{
                    background: "#e4aa3eff",
                    fontWeight: "bold",
                    "&:hover": { background: "#d4941eff" }
                }}
            >
                {isDownloading ? "Génération..." : "Télécharger le Badge"}
            </Button>

            {/* --- ZONE DU BADGE (partie imprimé) --- */}
            <div style={{ width: BADGE_WIDTH, height: BADGE_HEIGHT }}> {/* Wrapper pour forcer la taille exacte hors scaling */}
                <Paper
                    ref={badgeRef}
                    elevation={10}
                    sx={{
                        width: BADGE_WIDTH,
                        height: BADGE_HEIGHT,
                        backgroundColor: "#fff",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        borderRadius: 0, // Important pour l'impression (pas de coins ronds)
                    }}
                >
                    {/* 1. Header Graphique */}
                    <Box sx={{ height: "45mm", position: "relative" }}>
                        {/* Forme courbe décorative */}
                        <Box sx={{
                            position: "absolute", top: "-20%", left: "-10%", right: "-10%", height: "100%",
                            background: "linear-gradient(135deg, #e4aa3eff 0%, #d4941eff 100%)",
                            borderRadius: "0 0 50% 50%",
                            zIndex: 1
                        }} />

                        {/* Logo centré */}
                        <Box sx={{
                            position: "absolute", zIndex: 2, top: "5mm", width: "100%",
                            display: "flex", justifyContent: "center"
                        }}>
                            <img
                                crossOrigin="anonymous"
                                src={`${REACT_APP_LIFERAY_HOST}/${LOGO_ACCESS_PROD}`}
                                alt="Logo"
                                style={{ height: "25mm", objectFit: "contain" }}
                                onError={(e) => (e.target.style.display = 'none')}
                            />
                        </Box>
                    </Box>

                    {/* 2. Corps du Badge (Identité) */}
                    <Box sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center", // Centrer verticalement
                        mt: "-5mm", // Remonter un peu sur le header
                        px: "5mm",
                        textAlign: "center",
                        zIndex: 3
                    }}>

                        {/* Prénom */}
                        <Typography sx={{
                            fontSize: "24px",
                            fontWeight: 500,
                            color: "#333",
                            textTransform: "capitalize"
                        }}>
                            {participant.prenoms}
                        </Typography>

                        {/* NOM (Trés grand) */}
                        <Typography sx={{
                            fontSize: "28px",
                            fontWeight: 700,
                            color: "#000",
                            textTransform: "uppercase",
                            lineHeight: 1,
                            mb: 2
                        }}>
                            {participant.nom}
                        </Typography>

                        {/* Ligne de séparation */}
                        <Box sx={{ width: "40mm", height: "2px", background: "#e4aa3eff", mb: 2, mt: 2 }} />

                        {/* Profession / Organisation */}
                        <Typography sx={{
                            fontSize: "16px",
                            color: "#666",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            maxWidth: "100%",
                            wordWrap: "break-word"
                        }}>
                            {participant?.profession?.key != 'autre' ? participant?.profession?.name || "PARTICIPANT" : participant?.autreProfession || "PARTICIPANT"}
                        </Typography>
                        {/* Ligne de séparation */}
                        <Box sx={{ width: "40mm", height: "2px", background: "#e4aa3eff", mb: 2 }} />
                        <Typography sx={{
                            fontSize: "14px",
                            color: "#666",
                            fontWeight: 400,
                            textTransform: "uppercase",
                            maxWidth: "100%",
                            wordWrap: "break-word"
                        }}>
                            Ce badge vous permet de participer à la vente du {format2Date(participant?.[VENTE_RELATION_ACCESS]?.dateDeVente) || ""}
                        </Typography>
                        <Typography sx={{
                            fontSize: "14px",
                            color: "#666",
                            fontWeight: 400,
                            textTransform: "uppercase",
                            maxWidth: "100%",
                            wordWrap: "break-word"
                        }}>
                            Heure : {participant?.[VENTE_RELATION_ACCESS]?.heureDeVente || "-"}
                        </Typography>
                        <Typography sx={{
                            fontSize: "14px",
                            color: "#666",
                            fontWeight: 400,
                            textTransform: "uppercase",
                            maxWidth: "100%",
                            wordWrap: "break-word"
                        }}>
                            Lieu : {participant?.[VENTE_RELATION_ACCESS]?.lieuDeVente || "-"}
                        </Typography>
                    </Box>

                    {/* 3. Footer (QR Code) */}
                    <Box sx={{
                        height: "50mm",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        pb: "5mm"
                    }}>
                        <Box sx={{ p: 1, background: "#fff", border: "1px solid #ddd", borderRadius: 2 }}>
                            <QRCode value={participant.reference || "N/A"} size={100} />
                        </Box>

                        <Typography sx={{ mt: 1, fontSize: "10px", color: "#999", letterSpacing: 2 }}>
                            Votre ID : {participant.reference}
                        </Typography>
                    </Box>

                    {/* Bande colorée tout en bas (catégorie) */}
                    <Box sx={{ height: "8mm", width: "100%", background: "#2e7d32", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography sx={{
                            fontSize: "10px",
                            color: "#FFF",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            maxWidth: "100%",
                            textAlign: "center",
                            wordWrap: "break-word"
                        }}>
                            Code vente : {participant?.[VENTE_RELATION_ACCESS]?.externalReferenceCode || "-"}
                        </Typography>
                    </Box>

                </Paper>
            </div>
        </Box>
    );
};

export default BadgeA6;